import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_BASE, api } from "../lib/api";
import { formatCurrency, formatDate, STATUS_STYLES, warrantyStatus } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, Globe, Calendar, Package, Tag, ShieldCheck, ShieldAlert, Pencil, Save, FileText, MessageCircle, Loader2,
} from "lucide-react";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => { setC(r.data); setNotes(r.data.notes || ""); })
      .catch(() => setC(false));
  }, [id]);

  const shareWhatsapp = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/customers/${id}/share-token`);
      const invoiceUrl = `${API_BASE}/public/invoice/${data.token}`;
      const phone = (c.phone || "").replace(/[^\d]/g, "");
      const message = encodeURIComponent(
        `Merhaba ${c.first_name} ${c.last_name}, SwayDesign faturanızı buradan görüntüleyebilirsiniz:\n\n${invoiceUrl}`
      );
      const waUrl = phone
        ? `https://wa.me/${phone}?text=${message}`
        : `https://wa.me/?text=${message}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Paylaşım linki oluşturulamadı");
    } finally {
      setSharing(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/customers/${id}/notes`, { notes });
      toast.success("Notlar kaydedildi");
    } catch { toast.error("Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  if (c === null) return <Skeleton className="h-64" />;
  if (c === false) return <div className="text-red-500">Müşteri bulunamadı.</div>;

  const w = warrantyStatus(c.warranty_end_date);
  const s = STATUS_STYLES[c.status];

  return (
    <div className="space-y-6 max-w-5xl">
      <Button variant="ghost" onClick={() => nav(-1)} size="sm" className="text-muted-foreground -ml-3" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Geri
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Müşteri Detayı</div>
          <h1 className="text-4xl font-display font-semibold tracking-tight mt-1">{c.first_name} {c.last_name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className={`inline-flex items-center rounded-full ${s.bg} ${s.text} px-3 py-1 text-xs font-semibold tracking-wider`}>
              {c.status}
            </span>
            {w.active ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 px-3 py-1 text-xs font-semibold">
                <ShieldCheck className="h-3.5 w-3.5" /> Garanti aktif · {w.daysLeft} gün
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-1 text-xs font-semibold">
                <ShieldAlert className="h-3.5 w-3.5" /> Garanti dolmuş
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={shareWhatsapp} disabled={sharing} data-testid="whatsapp-share-btn" className="rounded-full bg-green-500 hover:bg-green-600 text-white border-green-500 hover:text-white">
            {sharing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
            WhatsApp Paylaş
          </Button>
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/customers/${id}/invoice`, '_blank')} data-testid="invoice-btn" className="rounded-full">
            <FileText className="h-4 w-4 mr-2" /> Fatura PDF
          </Button>
          <Button asChild data-testid="edit-customer-btn" className="rounded-full">
            <Link to={`/customers/${id}/edit`}><Pencil className="h-4 w-4 mr-2" /> Düzenle</Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">İletişim</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-muted-foreground" /> {c.email}</li>
            <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-muted-foreground" /> {c.phone}</li>
            <li className="flex items-center gap-3"><Globe className="h-4 w-4 text-muted-foreground" /> {c.country}</li>
          </ul>
        </Card>

        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">İş Bilgisi</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3"><Tag className="h-4 w-4 text-muted-foreground" /> {c.platform}</li>
            <li className="flex items-center gap-3"><Package className="h-4 w-4 text-muted-foreground" /> {c.package}</li>
            <li className="font-display text-2xl font-semibold pt-1">{formatCurrency(c.price)}</li>
          </ul>
        </Card>

        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Tarihler</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3"><Calendar className="h-4 w-4 text-muted-foreground" /> Başlama: <span className="ml-auto font-medium">{formatDate(c.start_date)}</span></li>
            <li className="flex items-center gap-3"><Calendar className="h-4 w-4 text-muted-foreground" /> Teslimat: <span className="ml-auto font-medium">{formatDate(c.delivery_date)}</span></li>
            <li className="flex items-center gap-3"><Calendar className="h-4 w-4 text-muted-foreground" /> Garanti: <span className="ml-auto font-medium">{formatDate(c.warranty_end_date)}</span></li>
          </ul>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Notlar</div>
            <div className="text-lg font-display font-semibold tracking-tight mt-1">İş Detayı & Özel Notlar</div>
          </div>
          <Button onClick={saveNotes} disabled={saving} data-testid="save-notes-btn" className="rounded-full">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
        <Textarea
          rows={10}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Bu müşteri için özel iş tanımı, referans linkler, teslim edilen dosyalar, tasarım notları..."
          data-testid="notes-textarea"
          className="resize-none"
        />
      </Card>
    </div>
  );
}
